import { useMemo } from 'react';

import { useMovementPageData } from '#src/app/pages/movement/pageContext';

type Social = 'facebook' | 'twitter' | 'email' | 'whatsapp';

// Mirrors the server's `ShareLink { channel, longUrl, shortUrl, shareId }` for the 4 channels
// selected by `MovementShareLinksContext.MOVEMENT_HEADER` (see Movement.shareLinks resolver).
type GeneratedShareIdSource = 'shortener' | 'server_shortener_failure';

export type ShareUrlInfo = Readonly<{
	url: string;
	trackingProperties: Readonly<{
		generated_share_id: string;
		generated_share_id_source: GeneratedShareIdSource;
	}>;
}>;

type ShareLink = Readonly<{
	channel: string;
	longUrl: string;
	shortUrl: string | null;
	shareId: string | null;
}>;

function toShareUrlInfo(link: ShareLink): ShareUrlInfo {
	return {
		url: link.shortUrl ?? link.longUrl,
		trackingProperties: {
			generated_share_id: link.shareId ?? '',
			generated_share_id_source: link.shareId ? 'shortener' : 'server_shortener_failure',
		},
	};
}

export function useShareUrl(): Record<Social, ShareUrlInfo> {
	const {
		data: {
			movement: { shareLinks },
		},
	} = useMovementPageData();

	return useMemo(() => {
		const getInfo = (channel: Social): ShareUrlInfo => {
			const link = shareLinks.links.find((candidate) => candidate.channel === channel);
			if (!link) throw new Error(`useShareUrl: no share link for channel "${channel}"`);
			return toShareUrlInfo(link);
		};
		return {
			facebook: getInfo('facebook'),
			twitter: getInfo('twitter'),
			email: getInfo('email'),
			whatsapp: getInfo('whatsapp'),
		};
	}, [shareLinks]);
}
